import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import supabase from '../utils/supabaseClient';

// Create context
const MessagingContext = createContext();

// Custom hook to use the messaging context
export const useMessaging = () => {
  return useContext(MessagingContext);
};

// Provider component
export const MessagingProvider = ({ children }) => {
  const { user } = useAuth();
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);

  // Fetch unread message count
  useEffect(() => {
    if (!user) {
      setUnreadCount(0);
      setLoading(false);
      return;
    }

    const fetchUnreadCount = async () => {
      try {
        // Count unread messages for the current user
        const { count, error } = await supabase
          .from('messages')
          .select('*', { count: 'exact', head: true })
          .eq('receiver_id', user.id)
          .eq('is_read', false);
        
        if (error) throw error;
        
        setUnreadCount(count || 0);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching unread messages:', err);
        setLoading(false);
      }
    };

    fetchUnreadCount();
    
    // Set up real-time subscription for new messages
    const subscription = supabase
      .channel('public:messages')
      .on('postgres_changes', { 
        event: 'INSERT', 
        schema: 'public', 
        table: 'messages',
        filter: `receiver_id=eq.${user.id}`
      }, payload => {
        // Increment unread count when a new message is received
        if (!payload.new.is_read) {
          setUnreadCount(prev => prev + 1);
        }
      })
      .on('postgres_changes', { 
        event: 'UPDATE', 
        schema: 'public', 
        table: 'messages',
        filter: `receiver_id=eq.${user.id}`
      }, payload => {
        // When a message is marked as read, recalculate the unread count
        fetchUnreadCount();
      })
      .subscribe();
    
    return () => {
      subscription.unsubscribe();
    };
  }, [user]);
  
  // Function to mark messages as read
  const markAsRead = async (messageIds) => {
    if (!user || !messageIds.length) return;
    
    try {
      const { error } = await supabase
        .from('messages')
        .update({ is_read: true })
        .in('id', messageIds);
      
      if (error) throw error;
      
      // After marking as read, update the unread count
      const { count, error: countError } = await supabase
        .from('messages')
        .select('*', { count: 'exact', head: true })
        .eq('receiver_id', user.id)
        .eq('is_read', false);
      
      if (countError) throw countError;
      
      setUnreadCount(count || 0);
    } catch (err) {
      console.error('Error marking messages as read:', err);
    }
  };

  // Expose the context value
  const value = {
    unreadCount,
    loading,
    markAsRead
  };

  return (
    <MessagingContext.Provider value={value}>
      {children}
    </MessagingContext.Provider>
  );
};

export default MessagingContext; 